import { useCallback, useEffect, useState } from "react";
import {
  getProject,
  listProjectDirectories,
  type Project,
  type ProjectDirectory,
} from "@/api/spaces";
import { Button } from "@/components/ui";
import { translate, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsProjectsMessages } from "@/i18n/messages/settings-projects";
import { useProjectStore } from "@/stores/project-store";
import { useSettingsNav } from "./settings-navigation";

export function projectError(error: unknown, fallback: string): string {
  const data = (error as { data?: Record<string, unknown> } | null)?.data;
  const value =
    data?.detail ?? data?.error ?? (data ? Object.values(data)[0] : null);
  return typeof value === "string"
    ? value
    : Array.isArray(value) && typeof value[0] === "string"
      ? value[0]
      : fallback;
}

export function refreshProjectNavigation() {
  void useProjectStore.getState().fetchAllProjects(true);
}

/** Scope directory lookup to its requested project, including direct links. */
export function useProjectPage(projectId: string, folderId?: string) {
  const [project, setProject] = useState<Project | null>(null);
  const [directories, setDirectories] = useState<ProjectDirectory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const retry = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);
    setProject(null);
    setDirectories([]);
    if (!projectId) {
      setLoading(false);
      return;
    }
    void Promise.all([getProject(projectId), listProjectDirectories(projectId)])
      .then(([next, folders]) => {
        if (!current) return;
        if (next.id !== projectId || next.status !== "active")
          throw new Error("Unavailable");
        setProject(next);
        setDirectories(folders);
      })
      .catch(() => {
        if (current)
          setError(translate(settingsProjectsMessages, "unavailable"));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [projectId, revision]);
  const folder = folderId
    ? (directories.find((item) => item.id === folderId) ?? null)
    : null;
  return {
    project,
    directories,
    setDirectories,
    folder,
    loading,
    error,
    retry,
  };
}

export function ProjectPageUnavailable({
  loading,
  retry,
}: {
  loading: boolean;
  retry: () => void;
}) {
  const t = useT(settingsProjectsMessages);
  const tc = useT(commonMessages);
  const nav = useSettingsNav();
  return (
    <div className="space-y-3 px-2 py-3" data-testid="project-page-unavailable">
      <p role="status" className="text-appkit-body text-muted">
        {loading ? tc("loading") : t("unavailable")}
      </p>
      {!loading && (
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            data-testid="project-page-retry"
            onClick={retry}
          >
            {tc("retry")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid="project-page-back"
            onClick={() => nav.open({ page: "projects" })}
          >
            {t("projects")}
          </Button>
        </div>
      )}
    </div>
  );
}
